// EventDevicesSelection – pick devices to associate with an event.

import React, { JSX, useEffect, useMemo, useState } from 'react';
import { formatTs } from '../../../helpers/helpers';
import { dispatchToast, dispatchToastError, formatApiResponseError, isDeviceAssignmentCasConflict, normalizeRaceStatus } from '../utils';
import useNonTerminalDeviceEventAssociations from '../../../hooks/useNonTerminalDeviceEventAssociations';
import type { EventRow } from '../types/EventsOverviewTypes';
import RefreshButton from '../../shared/RefreshButton';
import LoadingState from '../../shared/LoadingState';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import { INPUT } from '../../DeviceListView/utils/deviceListConstants';
import DeviceIdTableFilterBar, {
    deviceIdMatchesTableFilter,
} from '../../shared/DeviceIdTableFilterBar';
import { PortaledLayer } from '../../shared/PortaledLayer';
import InlineSpinner from '../../shared/InlineSpinner';

// ---- Types ----

type DeviceConfigureFormProps = {
    selectedIds: string[];
    onClose: () => void;
};

// ---- Component ----

export default function DeviceConfigureForm(
    props: DeviceConfigureFormProps,
): JSX.Element {
    const { selectedIds, onClose } = props;

    const {
        events,
        eventsLoading,
        deviceEventMap,
        reload,
    } = useNonTerminalDeviceEventAssociations(selectedIds);

    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const [eventRaces, setEventRaces] = useState<
        Array<{ raceId: string; name?: string; status?: string }>
    >([]);
    const [racesLoading, setRacesLoading] = useState(false);
    const [selectedRaceId, setSelectedRaceId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [deviceIdTableFilter, setDeviceIdTableFilter] = useState('');
    const [releaseRetryVisible, setReleaseRetryVisible] = useState(false);

    useEffect(() => {
        void reload();
    }, []);

    useEffect(() => {
        if (!selectedEventId || !window.eventsAPI?.listEventRaces) {
            setEventRaces([]);
            setSelectedRaceId(null);
            return;
        }
        setRacesLoading(true);
        void window.eventsAPI
            .listEventRaces(selectedEventId)
            .then((res) => {
                if (!res?.ok) {
                    setEventRaces([]);
                    setSelectedRaceId(null);
                    return;
                }
                const data = res.data as {
                    races?: Array<{ raceId?: string; name?: string; status?: string }>;
                };
                const rows = (data?.races || [])
                    .map((r) => ({
                        raceId: String(r.raceId || '').trim(),
                        name: r.name != null ? String(r.name) : undefined,
                        status: r.status != null ? String(r.status) : undefined,
                    }))
                    .filter((r) => r.raceId)
                    .filter((r) => normalizeRaceStatus(r.status) !== 'DRAFT');
                setEventRaces(rows);
                setSelectedRaceId(rows[0]?.raceId ?? null);
            })
            .catch(() => {
                setEventRaces([]);
                setSelectedRaceId(null);
            })
            .finally(() => setRacesLoading(false));
    }, [selectedEventId]);

    async function handleRefresh(): Promise<void> {
        await reload();
    }

    function handleSelectEvent(ev: EventRow): void {
        setSelectedEventId(ev.eventId);
        setReleaseRetryVisible(false);
    }

    /** Mutex elsewhere — user can release lock(s) and retry. Not used for CAS / optimistic conflicts. */
    function hasDeviceMutexLockConflict(res: { data?: unknown; error?: string | null }): boolean {
        const payload =
            res?.data && typeof res.data === 'object'
                ? (res.data as Record<string, unknown>)
                : null;
        if (payload?.error === 'device_already_assigned') return true;
        const pre = typeof res?.error === 'string' ? res.error : '';
        return pre.includes('device_already_assigned');
    }

    function getReleasableAssignments(): Array<{ eventId: string; raceId: string; deviceId: string }> {
        if (!selectedEventId || !selectedRaceId) return [];
        const out: Array<{ eventId: string; raceId: string; deviceId: string }> = [];
        for (const did of selectedIds) {
            const info = deviceEventMap[did];
            const srcEvent = String(info?.eventId || '').trim();
            const srcRace = String(info?.raceId || '').trim();
            if (!srcEvent || !srcRace) continue;
            if (srcEvent === selectedEventId && srcRace === selectedRaceId) continue;
            out.push({ eventId: srcEvent, raceId: srcRace, deviceId: did });
        }
        return out;
    }

    async function releaseExistingLocksAndRetryAssign(): Promise<void> {
        if (!window.eventsAPI || !selectedEventId || !selectedRaceId) return;
        const releasable = getReleasableAssignments();
        if (releasable.length === 0) {
            dispatchToast(
                'toast-error',
                'No releasable prior assignments found. Refresh and try again.',
            );
            return;
        }

        const grouped = new Map<string, { eventId: string; raceId: string; deviceIds: string[] }>();
        for (const row of releasable) {
            const key = `${row.eventId}#${row.raceId}`;
            const cur = grouped.get(key);
            if (cur) {
                cur.deviceIds.push(row.deviceId);
            } else {
                grouped.set(key, {
                    eventId: row.eventId,
                    raceId: row.raceId,
                    deviceIds: [row.deviceId],
                });
            }
        }

        setSubmitting(true);
        try {
            for (const g of grouped.values()) {
                const releaseRes = await window.eventsAPI.releaseRaceDeviceLocks(
                    g.eventId,
                    g.raceId,
                    g.deviceIds,
                );
                if (!releaseRes?.ok) {
                    dispatchToast(
                        'toast-error',
                        `Failed to release previous assignment locks: ${formatApiResponseError(releaseRes)}`,
                    );
                    return;
                }
            }

            const assignRes = await window.eventsAPI.assignDevices(selectedEventId, {
                deviceIds: selectedIds,
                raceId: selectedRaceId,
            });
            if (!assignRes?.ok) {
                if (isDeviceAssignmentCasConflict(assignRes)) {
                    setReleaseRetryVisible(false);
                    await reload();
                    dispatchToast(
                        'toast-error',
                        `Failed to assign devices: ${formatApiResponseError(assignRes)}`,
                    );
                    return;
                }
                dispatchToast(
                    'toast-error',
                    `Failed to assign devices: ${formatApiResponseError(assignRes)}`,
                );
                setReleaseRetryVisible(hasDeviceMutexLockConflict(assignRes));
                return;
            }

            dispatchToast(
                'toast-info',
                `Released prior locks and assigned ${selectedIds.length} device${selectedIds.length === 1 ? '' : 's'}`,
            );
            setReleaseRetryVisible(false);
            onClose();
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSubmit(): Promise<void> {
        if (!selectedEventId) {
            dispatchToast('toast-error', 'Select an event first');
            return;
        }

        if (!window.eventsAPI) {
            dispatchToast('toast-error', 'Events API is not available');
            return;
        }

        if (selectedIds.length === 0) {
            dispatchToast('toast-error', 'No devices selected');
            return;
        }

        if (!selectedRaceId) {
            dispatchToast(
                'toast-error',
                'Select a race (create one in the event overview if none exist)',
            );
            return;
        }

        setSubmitting(true);
        setReleaseRetryVisible(false);
        try {
            const res = await window.eventsAPI.assignDevices(selectedEventId, {
                deviceIds: selectedIds,
                raceId: selectedRaceId,
            });

            if (!res.ok) {
                if (isDeviceAssignmentCasConflict(res)) {
                    setReleaseRetryVisible(false);
                    await reload();
                    dispatchToast(
                        'toast-error',
                        `Failed to assign devices: ${formatApiResponseError(res)}`,
                    );
                    return;
                }
                if (hasDeviceMutexLockConflict(res)) {
                    setReleaseRetryVisible(true);
                }
                dispatchToast(
                    'toast-error',
                    `Failed to assign devices: ${formatApiResponseError(res)}`,
                );
                return;
            }

            dispatchToast(
                'toast-info',
                `Assigned ${selectedIds.length} device${selectedIds.length === 1 ? '' : 's'} to event`,
            );

            onClose();
        } catch (err: any) {
            dispatchToastError(err, 'Failed to assign devices');
        } finally {
            setSubmitting(false);
        }
    }

    const uniqueEventsForSelected = (() => {
        const ids = new Set<string>();
        for (const id of selectedIds) {
            const ev = deviceEventMap[id];
            if (ev?.eventId) {
                ids.add(ev.eventId);
            }
        }
        return ids;
    })();

    const visibleSelectedIds = useMemo(
        () => selectedIds.filter((id) => deviceIdMatchesTableFilter(id, deviceIdTableFilter)),
        [selectedIds, deviceIdTableFilter],
    );

    return (
        <PortaledLayer>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
                <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                            Offline operations
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            {selectedIds.length} device
                            {selectedIds.length === 1 ? '' : 's'} selected
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-slate-300"
                    >
                        Close
                    </button>
                </div>

                <div className="space-y-4 overflow-y-auto px-4 py-3 text-xs">
                    <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3 bg-gray-50 dark:bg-slate-800">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                                <div className="font-semibold text-gray-900 dark:text-slate-100 text-xs">
                                    Current event association
                                </div>
                                <RefreshButton
                                    busy={eventsLoading}
                                    onClick={() => void handleRefresh()}
                                    className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                                    busyText="Refreshing..."
                                />
                            </div>
                            {eventsLoading && (
                                <LoadingState label="Checking..." />
                            )}
                        </div>

                        {selectedIds.length === 0 ? (
                            <div className="text-[11px] text-gray-500 dark:text-slate-400">
                                No devices selected.
                            </div>
                        ) : (
                            <TableLoadingOverlay loading={eventsLoading} label="Checking...">
                            <DeviceIdTableFilterBar
                                value={deviceIdTableFilter}
                                onChange={setDeviceIdTableFilter}
                                enableScanner
                                inputClassName={`${INPUT} min-w-[10rem] max-w-xs`}
                                disabled={eventsLoading}
                                className="mb-2"
                            />
                            <div className="max-h-32 overflow-auto border border-gray-100 dark:border-slate-700 rounded bg-white dark:bg-slate-900">
                                <table className="min-w-full text-[11px]">
                                    <thead className="bg-gray-50 dark:bg-slate-800">
                                        <tr>
                                            <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-slate-300">
                                                Device ID
                                            </th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-slate-300">
                                                Event
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                                        {visibleSelectedIds.length === 0 ? (
                                            <tr>
                                                <td className="px-2 py-2 text-[11px] text-gray-500 dark:text-slate-400" colSpan={2}>
                                                    {selectedIds.length === 0
                                                        ? 'No devices selected.'
                                                        : 'No devices match the filter.'}
                                                </td>
                                            </tr>
                                        ) : null}
                                        {visibleSelectedIds.map((id) => {
                                                const ev = deviceEventMap[id];
                                            return (
                                                <tr key={id}>
                                                    <td className="px-2 py-1 font-mono break-all text-gray-900 dark:text-slate-100">
                                                        {id}
                                                    </td>
                                                        <td className="px-2 py-1 text-gray-700 dark:text-slate-300">
                                                            {ev ? (
                                                            <span>
                                                                    <span>
                                                                        {ev.name || ev.eventId}{' '}
                                                                        <span className="font-mono text-[10px] text-gray-500 dark:text-slate-400">
                                                                            ({ev.eventId})
                                                                        </span>
                                                                        {ev.raceId ? (
                                                                            <span className="ml-1 text-[10px] text-gray-500 dark:text-slate-400">
                                                                                · {ev.raceId}
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                            </span>
                                                            ) : (
                                                                <span className="text-gray-400 dark:text-slate-400">Not assigned</span>
                                                            )}
                                                        </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            </TableLoadingOverlay>
                        )}

                        {uniqueEventsForSelected.size > 1 && (
                            <p className="mt-2 text-[11px] text-amber-600">
                                Selected devices are currently in multiple events.
                            </p>
                        )}
                    </div>

                    <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
                        <div className="text-[11px] font-semibold text-gray-900 dark:text-slate-100 mb-1">
                            Add devices to an event
                        </div>
                        <p className="text-[11px] text-gray-600 dark:text-slate-400">
                            Removing devices from an event is only available from the event&apos;s device and athlete assignment dialog.
                        </p>
                    </div>

                    <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                                Choose event
                            </h3>
                            {eventsLoading && (
                                <LoadingState label="Loading events..." />
                            )}
                        </div>

                        {events.length === 0 && !eventsLoading ? (
                            <div className="text-[11px] text-gray-500 dark:text-slate-400">
                                No events found.
                            </div>
                        ) : (
                            <TableLoadingOverlay loading={eventsLoading} label="Loading events...">
                            <div className="max-h-56 overflow-auto border border-gray-100 dark:border-slate-700 rounded bg-gray-50 dark:bg-slate-800">
                                <table className="min-w-full text-[11px]">
                                    <thead className="bg-gray-100 dark:bg-slate-800 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                                Name
                                            </th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                                Event ID
                                            </th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                                Start
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                                        {events.map((ev) => {
                                            const isSelected = selectedEventId === ev.eventId;
                                            return (
                                                <tr
                                                    key={ev.eventId}
                                                    onClick={() => handleSelectEvent(ev)}
                                                    className={
                                                        'cursor-pointer hover:bg-blue-50 ' +
                                                        (isSelected ? 'bg-blue-50' : '')
                                                    }
                                                >
                                                    <td className="px-3 py-2">
                                                        <div className="font-medium text-gray-900 dark:text-slate-100">
                                                            {ev.name || '(untitled)'}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <span className="font-mono text-[11px] break-all text-gray-700 dark:text-slate-300">
                                                            {ev.eventId}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-slate-300">
                                                        {formatTs(ev.startAt as number | undefined)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            </TableLoadingOverlay>
                        )}

                        <div className="mt-3 border border-gray-100 dark:border-slate-700 rounded-lg p-3 bg-gray-50 dark:bg-slate-800">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">Choose race</h3>
                                {racesLoading && <LoadingState label="Loading races..." />}
                            </div>
                            {!selectedEventId ? (
                                <p className="text-[11px] text-gray-500 dark:text-slate-400">Select an event first.</p>
                            ) : racesLoading ? null : eventRaces.length === 0 ? (
                                <p className="text-[11px] text-amber-700">
                                    No assignable races in this event (Draft races use the athlete list only).
                                    Create a race and move it to Device Packaging, then try again.
                                </p>
                            ) : (
                                <select
                                    value={selectedRaceId ?? ''}
                                    onChange={(e) =>
                                        setSelectedRaceId(e.target.value.trim() || null)
                                    }
                                    className={`${INPUT} w-full max-w-md text-[11px]`}
                                >
                                    {eventRaces.map((r) => (
                                        <option key={r.raceId} value={r.raceId}>
                                            {r.name ? `${r.name} (${r.raceId})` : r.raceId}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">
                            Select an event and race, then click Submit to assign the selected devices to that race.
                        </p>
                        {releaseRetryVisible ? (
                            <div className="mt-3 rounded border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/40 px-3 py-2">
                                <div className="text-[11px] text-amber-900">
                                    One or more selected devices are locked to another race. Release prior locks and retry?
                                </div>
                                <button
                                    type="button"
                                    onClick={() => void releaseExistingLocksAndRetryAssign()}
                                    disabled={submitting}
                                    className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-400 bg-white dark:bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                                >
                                    {submitting ? (
                                        <>
                                            <InlineSpinner size="xs" variant="current" />
                                            Releasing…
                                        </>
                                    ) : (
                                        'Release previous lock(s) and retry'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-[11px]">
                    <div className="text-gray-500 dark:text-slate-400">
                        {selectedEventId
                            ? `Event: ${selectedEventId}${
                                  selectedRaceId ? ` · Race: ${selectedRaceId}` : ''
                              }`
                            : 'No event selected'}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-[11px] font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleSubmit()}
                            disabled={
                                submitting ||
                                !selectedEventId ||
                                !selectedRaceId ||
                                eventRaces.length === 0
                            }
                            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                            {submitting ? (
                                <>
                                    <InlineSpinner size="xs" variant="current" />
                                    Submitting…
                                </>
                            ) : (
                                'Submit'
                            )}
                        </button>
                    </div>
                </div>
            </div>
            </div>
        </PortaledLayer>
    );
}
